import React, { useState } from 'react'
import Modal from './Modal'
import { Field, inputClass } from './Field'
import { DEFAULT_LOW_BALANCE_ALERT, LOW_BALANCE_DAYS, PlanSettings } from '../types'

interface Props {
  settings: PlanSettings
  /** The combined thresholds only mean something once there's more than one account. */
  showCombined: boolean
  onSave: (s: Partial<PlanSettings>) => void
  onClose: () => void
}

/** Settings shared by every account. Each account's own live in AccountForm. */
export default function SettingsForm({ settings, showCombined, onSave, onClose }: Props) {
  const [bad, setBad] = useState(settings.combinedRagThresholds.bad.toString())
  const [medium, setMedium] = useState(settings.combinedRagThresholds.medium.toString())
  const [currencySymbol, setCurrencySymbol] = useState(settings.currencySymbol)
  const [projectHistory, setProjectHistory] = useState(settings.projectHistory)
  const alert = settings.lowBalanceAlert ?? DEFAULT_LOW_BALANCE_ALERT
  const [alertOn, setAlertOn] = useState(alert.enabled)
  const [alertDays, setAlertDays] = useState(alert.days.toString())
  const [alertThreshold, setAlertThreshold] = useState(alert.threshold?.toString() ?? '')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSave({
      combinedRagThresholds: { bad: parseFloat(bad) || 0, medium: parseFloat(medium) || 0 },
      currencySymbol: currencySymbol || '£',
      projectHistory,
      lowBalanceAlert: {
        enabled: alertOn,
        days: Math.min(LOW_BALANCE_DAYS.max, Math.max(LOW_BALANCE_DAYS.min, Math.round(Number(alertDays) || DEFAULT_LOW_BALANCE_ALERT.days))),
        // Blank follows the combined "tight" level, so it moves when that does.
        ...(alertThreshold.trim() !== '' && Number.isFinite(Number(alertThreshold)) ? { threshold: Number(alertThreshold) } : {}),
      },
    })
  }

  return (
    <Modal title="Settings" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <label className="flex items-start gap-2 mb-4 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={projectHistory}
            onChange={(e) => setProjectHistory(e.target.checked)}
          />
          <span className="text-xs font-sans text-ink/80">
            Project a balance for days before the starting balance date
            <span className="block text-[11px] text-ink/70 mt-0.5">
              Off by default — those days are greyed out and can't be opened, since there's no
              real starting point to work backward from. Turn this on to see a speculative trend
              anyway.
            </span>
          </span>
        </label>

        <Field label="Currency symbol">
          <input className={inputClass} value={currencySymbol} onChange={(e) => setCurrencySymbol(e.target.value)} maxLength={3} />
        </Field>

        <div className="border-t border-line pt-3 mt-1 mb-1">
          <label className="flex items-start gap-2 mb-3 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={alertOn} onChange={(e) => setAlertOn(e.target.checked)} />
            <span className="text-xs font-sans text-ink/80">
              Warn me when my balance is heading too low
              <span className="block text-[11px] text-ink/70 mt-0.5">
                A banner at the top of the app when the combined balance of every account counted in the total drops
                below the level below at any point in the days ahead.
              </span>
            </span>
          </label>
          {alertOn && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Days ahead to check">
                <input
                  className={inputClass}
                  type="number"
                  min={LOW_BALANCE_DAYS.min}
                  max={LOW_BALANCE_DAYS.max}
                  step="1"
                  value={alertDays}
                  onChange={(e) => setAlertDays(e.target.value)}
                />
              </Field>
              <Field label="Warn below" hint={`Blank uses the “tight” level (${settings.currencySymbol}${settings.combinedRagThresholds.medium})`}>
                <input
                  className={inputClass}
                  type="number"
                  step="0.01"
                  value={alertThreshold}
                  placeholder={String(settings.combinedRagThresholds.medium)}
                  onChange={(e) => setAlertThreshold(e.target.value)}
                />
              </Field>
            </div>
          )}
        </div>

        {showCombined && (
          <div className="border-t border-line pt-3 mt-1">
            <p className="text-xs text-ink/75 mb-3">
              RAG thresholds for the combined balance of every account counted in the total. Each account has its own,
              under Edit account.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Bad threshold (≤)">
                <input className={inputClass} type="number" step="0.01" value={bad} onChange={(e) => setBad(e.target.value)} />
              </Field>
              <Field label="Tight threshold (≤)">
                <input className={inputClass} type="number" step="0.01" value={medium} onChange={(e) => setMedium(e.target.value)} />
              </Field>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t border-line">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-sm border border-line text-ink/80 hover:bg-paper">
            Cancel
          </button>
          <button type="submit" className="px-3 py-1.5 text-sm rounded-sm bg-accent text-white hover:bg-accent/90">
            Save
          </button>
        </div>
      </form>
    </Modal>
  )
}
